import {
  fetchAnalysisSectionDataVersion2,
  filterEndDate,
  filterStartDate,
  getAppliedFilters,
  getDimension,
  overallGroupByFilter,
  overallIndicatorFilter,
  overallTimeFilter,
} from 'redux/PlanPageSlice';
import { GetInputAdjustments } from 'redux/driverAdjustmentSlice';
import {
  fetchCompareAnalysisSectionData,
  fetchCompareTableData,
  getBaseScenario,
  getCompareScenario,
} from 'redux/ScenarioSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

interface BuildFetchParametersOptions {
  blockId: string | undefined;
  baseScenarioId: number | undefined;
  selectedGroupBy: string | number | null;
  dimension: { id?: number } | null;
  dateFilterSelected: boolean;
  dateFilter: string;
  selectedYear: string | null;
  appliedFilters: unknown;
  inputAdjustments: Record<string, unknown> | null;
  indicatorFilter: string;
  compareScenarioId?: number;
}

export const resolveDimFilter = (appliedFilters: unknown) => {
  if (!Array.isArray(appliedFilters)) return [];
  return appliedFilters;
};

export const buildTimeFilter = (
  dateFilterSelected: boolean,
  dateFilter: string,
  selectedYear: string | null,
) => {
  if (dateFilterSelected) return dateFilter;
  if (!selectedYear || selectedYear === 'all') return null;
  return selectedYear;
};

export const buildDimId = (
  selectedGroupBy: string | number | null,
  dimension: { id?: number } | null,
) => {
  if (selectedGroupBy === 'Total') return null;
  if (dimension && !selectedGroupBy) return dimension?.id ?? null;
  if (selectedGroupBy) return Number.parseInt(String(selectedGroupBy), 10);
  return null;
};

export const buildAnalysisParameters = ({
  baseScenarioId,
  selectedGroupBy,
  dimension,
  dateFilterSelected,
  dateFilter,
  selectedYear,
  appliedFilters,
  inputAdjustments,
  indicatorFilter,
  compareScenarioId,
}: BuildFetchParametersOptions) => {
  const baseParameters = {
    dim_id: buildDimId(selectedGroupBy, dimension),
    dim_sort: 'ASC' as const,
    time_filter: buildTimeFilter(dateFilterSelected, dateFilter, selectedYear),
    time_aggregation: 'Y,Q,M',
    scenario_id: baseScenarioId,
    dim_filter: JSON.stringify(resolveDimFilter(appliedFilters)),
    ...(inputAdjustments !== null &&
      Object.keys(inputAdjustments).length > 0 && {
        input_adjustments: inputAdjustments,
      }),
  };

  if (compareScenarioId) {
    return {
      ...baseParameters,
      indicator_filter: indicatorFilter,
      compare_scenario_id: compareScenarioId,
    };
  }

  return baseParameters;
};

export const buildCompareTableParameters = (options: BuildFetchParametersOptions) => {
  const analysisParameters = buildAnalysisParameters(options);
  const { baseScenarioId, compareScenarioId, indicatorFilter, inputAdjustments } = options;

  return {
    dim_id: analysisParameters.dim_id,
    dim_sort: analysisParameters.dim_sort,
    time_aggregation: analysisParameters.time_aggregation,
    indicator_filter: indicatorFilter,
    scenario_id: baseScenarioId,
    compare_scenario_id: compareScenarioId,
    dim_filter: analysisParameters.dim_filter,
    ...(inputAdjustments !== null &&
      Object.keys(inputAdjustments).length > 0 && {
        input_adjustments: inputAdjustments,
      }),
  };
};

export const fetchPlanPageAnalysisData = (
  dispatch: AppDispatch,
  options: BuildFetchParametersOptions,
) => {
  const { blockId, compareScenarioId, baseScenarioId } = options;
  if (!blockId || !baseScenarioId) return;

  if (compareScenarioId) {
    const chartParameters = buildAnalysisParameters(options);
    const tableParameters = buildCompareTableParameters(options);

    dispatch(
      fetchCompareAnalysisSectionData({
        blockId: Number(blockId),
        parameters: { params: chartParameters },
      }),
    );
    dispatch(
      fetchCompareTableData({
        blockId: Number(blockId),
        parameters: { params: tableParameters },
      }),
    );
    return;
  }

  dispatch(
    fetchAnalysisSectionDataVersion2({
      blockId: Number(blockId),
      parameters: buildAnalysisParameters(options),
    }),
  );
};

export const refetchPlanPageFromStore = (
  dispatch: AppDispatch,
  getState: () => unknown,
  blockId?: string,
) => {
  const state = getState();
  const baseScenario = getBaseScenario(state);
  if (!blockId || !baseScenario?.id) return;

  const startDate = filterStartDate(state);
  const endDate = filterEndDate(state);
  const dateFilterSelected = Boolean(startDate && endDate);

  const compareScenario = getCompareScenario(state);
  const compareScenarioId = compareScenario?.id;

  fetchPlanPageAnalysisData(dispatch, {
    blockId,
    baseScenarioId: baseScenario.id,
    selectedGroupBy: overallGroupByFilter(state),
    dimension: getDimension(state),
    dateFilterSelected,
    dateFilter: `[{ 'start_date': '${startDate}', 'end_date': '${endDate}' }]`,
    selectedYear: overallTimeFilter(state),
    appliedFilters: getAppliedFilters(state),
    inputAdjustments: GetInputAdjustments(state),
    indicatorFilter: overallIndicatorFilter(state),
    compareScenarioId,
  });
};
